import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import {
  authCredentialsSchema,
  loginToPortal,
  type AuthCredentials,
} from '@/features/auth/login/api'
import { resolveLoginErrorMessage, resolveLoginFailureMessage } from '@/features/auth/login/errors'
import {
  clearAuthenticatedSession,
  persistAuthenticatedSession,
} from '@/features/auth/login/session'
import {
  normalizeCredentials,
  validateEmailValue,
  validatePasswordValue,
} from '@/features/auth/login/validation'
import { consumeOAuthAuthorizationReturnUrl } from '@/features/auth/oauth/authorization-resume'
import { getPublicSsoConfig, getUserSsoRedirectUrl } from '@/features/auth/sso/user-api'
import { normalizeLanguage } from '@/i18n/i18n'
import {
  isPortalPasswordResetRequired,
  portalPasswordResetRoutePath,
} from '@/lib/auth/portal-routes'
import {
  consumePortalLoginPrefillEmail,
  consumeUserPostLoginReturnPath,
  persistUserPostLoginReturnPath,
  wecomBindingLoginResumeMarker,
} from '@/lib/auth/session-storage'
import type { PortalKind } from '@/lib/auth/token-storage'

export function usePortalLoginPage(
  portal: PortalKind,
  mobile = false,
  loginMode: 'default' | 'password' = 'default',
) {
  const { i18n, t } = useTranslation('auth')
  const { showFeedback } = useFeedback()
  const language = normalizeLanguage(i18n.language)
  const isUserPortal = portal === 'user'
  const isRecoveryLogin = isUserPortal && loginMode === 'password'
  const autoRedirectAttemptedRef = useRef(false)
  const [autoRedirectFailed, setAutoRedirectFailed] = useState(false)
  const [loginNotice] = useState<'resetSuccess' | 'wecomResume' | null>(() => {
    if (!mobile) return null
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('reset') === 'success') return 'resetSuccess'
    return searchParams.get('resume') === wecomBindingLoginResumeMarker ? 'wecomResume' : null
  })

  const form = useForm<AuthCredentials>({
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  })
  const {
    clearErrors,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setError,
    setFocus,
    setValue,
  } = form

  useEffect(() => {
    const email = consumePortalLoginPrefillEmail(portal)
    if (!email) return
    setValue('email', email, { shouldDirty: false, shouldTouch: false, shouldValidate: false })
    window.requestAnimationFrame(() => setFocus('password'))
  }, [portal, setFocus, setValue])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    if (isUserPortal && searchParams.get('resume') !== wecomBindingLoginResumeMarker) {
      persistUserPostLoginReturnPath(null)
    }
    if (searchParams.get('reset') === 'success') {
      if (!mobile) {
        showFeedback({ message: t('passwordReset.successLoginPrompt'), tone: 'success' })
      }
      searchParams.delete('reset')
      const remainingSearch = searchParams.toString()
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${remainingSearch ? `?${remainingSearch}` : ''}`,
      )
    }
  }, [isUserPortal, mobile, showFeedback, t])

  useEffect(() => {
    if (isPortalPasswordResetRequired(portal)) {
      setError('root', { message: t('common.passwordResetRequired') })
    }
  }, [portal, setError, t])

  const clearFieldFeedback = (field: keyof AuthCredentials) => {
    clearErrors(field)
    clearErrors('root')
  }
  const validateEmailOnBlur = () => {
    const message = validateEmailValue(getValues('email'), t)
    if (message) setError('email', { message })
  }
  const validatePasswordOnBlur = () => {
    const message = validatePasswordValue(getValues('password'), t)
    if (message) setError('password', { message })
  }

  const loginMutation = useMutation({
    mutationFn: (credentials: AuthCredentials) =>
      loginToPortal(portal, credentials, { recovery: isRecoveryLogin }),
  })
  const ssoConfigQuery = useQuery({
    enabled: isUserPortal,
    queryFn: getPublicSsoConfig,
    queryKey: ['public-sso-config'],
    staleTime: 30_000,
  })
  const showEmailPasswordForm =
    isRecoveryLogin || !isUserPortal || ssoConfigQuery.data?.email_password_enabled !== false
  const showSsoLogin = isUserPortal && ssoConfigQuery.data?.web_sso_enabled === true
  const showLoginDivider = showEmailPasswordForm && showSsoLogin
  const noLoginMethods =
    isUserPortal && ssoConfigQuery.isSuccess && !showEmailPasswordForm && !showSsoLogin
  const shouldShrinkUserLoginCard = isUserPortal && !showSsoLogin && !ssoConfigQuery.isLoading
  const shouldAutoRedirectToSso =
    isUserPortal &&
    !isRecoveryLogin &&
    !autoRedirectFailed &&
    ssoConfigQuery.data?.web_sso_enabled === true &&
    ssoConfigQuery.data.sso_login_behavior === 'redirect'
  const loginEntryPending =
    isUserPortal && !isRecoveryLogin && (ssoConfigQuery.isLoading || shouldAutoRedirectToSso)

  const ssoMutation = useMutation({
    mutationFn: async (replaceLocation: boolean) => ({
      replaceLocation,
      response: await getUserSsoRedirectUrl(),
    }),
    onSuccess({ replaceLocation, response }) {
      if (response.code === 'success' && response.data.auth_url) {
        if (replaceLocation) {
          window.location.replace(response.data.auth_url)
        } else {
          window.location.assign(response.data.auth_url)
        }
        return
      }
      if (replaceLocation) setAutoRedirectFailed(true)
      const errorMessage = 'message' in response ? response.message : undefined
      setError('root', { message: errorMessage ?? t('common.networkError') })
    },
    onError(_error, replaceLocation) {
      if (replaceLocation) setAutoRedirectFailed(true)
      setError('root', { message: t('common.networkError') })
    },
  })

  useEffect(() => {
    if (!shouldAutoRedirectToSso || autoRedirectAttemptedRef.current) return
    autoRedirectAttemptedRef.current = true
    ssoMutation.mutate(true)
  }, [shouldAutoRedirectToSso, ssoMutation])

  const onSubmit = handleSubmit(async (values) => {
    const emailMessage = validateEmailValue(values.email, t)
    const passwordMessage = validatePasswordValue(values.password, t)
    if (emailMessage) setError('email', { message: emailMessage })
    if (passwordMessage) setError('password', { message: passwordMessage })
    if (emailMessage || passwordMessage) return

    const parsed = authCredentialsSchema.safeParse(normalizeCredentials(values))
    if (!parsed.success) {
      setError('root', { message: t('common.validation.emailInvalid') })
      return
    }

    try {
      const response = await loginMutation.mutateAsync(parsed.data)
      if (response.code === 'fail') {
        setError('root', {
          message: resolveLoginFailureMessage(response.message ?? response.data, t),
        })
        return
      }
      const session = persistAuthenticatedSession(portal, response)
      if (session?.passwordResetRequired) {
        const recoverySearch = isRecoveryLogin ? '?login_entry=password' : ''
        window.location.assign(`${portalPasswordResetRoutePath[portal]}${recoverySearch}`)
        return
      }
      window.location.assign(
        (isUserPortal ? consumeOAuthAuthorizationReturnUrl() : undefined) ??
          (isUserPortal ? consumeUserPostLoginReturnPath() : undefined) ??
          session?.redirectPath ??
          '/',
      )
    } catch (error) {
      clearAuthenticatedSession(portal)
      setError('root', { message: resolveLoginErrorMessage(error, t) })
    }
  })

  return {
    clearFieldFeedback,
    errors,
    isUserPortal,
    language,
    loginMutation,
    loginEntryPending,
    loginNotice,
    noLoginMethods,
    onSubmit,
    register,
    shouldShrinkUserLoginCard,
    showEmailPasswordForm,
    showLoginDivider,
    showSsoLogin,
    ssoMutation: {
      ...ssoMutation,
      mutate: () => ssoMutation.mutate(false),
    },
    t,
    validateEmailOnBlur,
    validatePasswordOnBlur,
  }
}
